import type { WhatsAppInboxMessage } from '@zrsystem/contracts';
import type { ReactElement } from 'react';

import { FormattedChatText } from '@/features/conversation/components/formatted-chat-text.js';
import { WhatsAppInboxDocumentCard } from '@/features/notification/components/whatsapp-inbox-document-card.js';
import { useWhatsAppInboxMediaObjectUrl } from '@/features/notification/hooks/use-whatsapp-inbox-media-object-url.js';
import {
  presentWhatsAppInboxDocument,
  whatsAppInboxDocumentCaption,
} from '@/features/notification/lib/whatsapp-inbox-document.js';
import { formatWhatsAppInboxTimestamp } from '@/features/notification/lib/whatsapp-inbox-timestamp.js';

export interface WhatsAppInboxFileBubbleProps {
  readonly message: WhatsAppInboxMessage;
}

function resolveFileUrl(message: WhatsAppInboxMessage): string | null {
  const mediaUrl = message.media?.url ?? message.mediaUrl;
  if (mediaUrl === null || mediaUrl.length === 0) {
    return null;
  }
  return mediaUrl;
}

export function WhatsAppInboxFileBubble({ message }: WhatsAppInboxFileBubbleProps): ReactElement {
  const sourceUrl = resolveFileUrl(message);
  const mimetype = message.media?.mimetype ?? 'application/octet-stream';
  const filename = message.media !== null ? message.media.filename : 'Arquivo';
  const media = useWhatsAppInboxMediaObjectUrl(sourceUrl, mimetype);
  const presentation = presentWhatsAppInboxDocument(filename, mimetype);
  const caption = whatsAppInboxDocumentCaption(message.body, filename, presentation.title);
  const fileUrl = media.src;

  return (
    <div className="whatsapp-inbox-bubble__media">
      {media.pending ? <p>Carregando documento…</p> : null}
      {presentation.isPdf && fileUrl !== null ? (
        <iframe
          className="whatsapp-inbox-doc-preview"
          title={presentation.title}
          src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
        />
      ) : null}
      <WhatsAppInboxDocumentCard
        filename={filename}
        mimetype={mimetype}
        href={fileUrl}
        {...(sourceUrl !== null && !media.pending && fileUrl === null
          ? { detail: 'indisponível' }
          : {})}
      />
      {caption !== null ? (
        <p>
          <FormattedChatText text={caption} />
        </p>
      ) : null}
      <time className="whatsapp-inbox-bubble__time" dateTime={String(message.timestamp)}>
        {formatWhatsAppInboxTimestamp(message.timestamp)}
      </time>
    </div>
  );
}
